import { useEffect } from "react";
import type { RouteObject } from "react-router-dom";
import { useLocation, Navigate } from "react-router-dom";
import { type ReactNode } from "react";

/** Redirects browser to an external URL (opens same tab). */
function ExternalRedirect({ url }: { url: string }) {
  useEffect(() => { window.location.href = url; }, [url]);
  return null;
}
import NotFound from "../pages/NotFound";
import Dashboard from "../pages/dashboard/page";
import Profile from "../pages/profile/page";
import ProfileAccount from "../pages/profile/account/page";
import Login from "../pages/login/page";
import Register from "../pages/register/page";
import ForgotPassword from "../pages/forgotPassword/page";
import Onboarding from "../pages/onboarding/page";
import SetupPage from "../pages/setup/page";
import WatchlistPage from "../pages/watchlist/page";
import Positions from "../pages/positions/page";
import StockDetail from "../pages/stockDetail/page";
import DecisionDetail from "../pages/decisionDetail/page";
import AITrader from "../pages/ai-trader/page";
import AITraderMemory from "../pages/ai-trader/memory/page";
import AIStrategy from "../pages/ai-strategy/page";
import AIStrategyCreate from "../pages/ai-strategy/create";
import AIStrategyEdit from "../pages/ai-strategy/edit";
import AIStrategyDetail from "../pages/ai-strategy/detail";
import AIStrategyBacktest from "../pages/ai-strategy/backtest";
import DashboardFlowPage from "../pages/dashboard/flow/page";
import NewsHome from "../pages/news/home/page";
import ResearchHome from "../pages/research/page";
import ResearchGenerate from "../pages/research/generate";
import ResearchReport from "../pages/research/detail";
import ResearchLibrary from "../pages/research/library";
import ProfileSubscription from "../pages/profile/subscription";
import ProfileStats from "../pages/profile/stats";
import Notifications from "../pages/notifications/page";
import Achievements from "../pages/achievements/page";
import Calendar from "../pages/calendar/page";
import Reports from "../pages/reports/page";
import About from "../pages/profile/about/page";
import AboutUser from "../pages/profile/about/user/page";
import AboutPrivacy from "../pages/profile/about/privacy/page";
import AboutRisk from "../pages/profile/about/risk/page";
import AboutContact from "../pages/profile/about/contact/page";
import AppShell from "../components/feature/AppShell";
import BottomTabBar from "../components/feature/BottomTabBar";

function TabLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const token = localStorage.getItem("alpha_token") || localStorage.getItem("token");
  if (!token) return <Navigate to="/login" replace />;
  return (
    <div className="fixed inset-0 flex flex-col bg-background-50">
      <div className="flex-1 overflow-y-auto">
        <AppShell>
          <div key={location.pathname} className="animate-fade-in flex-1 flex flex-col pb-[120px]">
            {children}
          </div>
        </AppShell>
      </div>
      <BottomTabBar className="max-sm:flex lg:hidden" />
    </div>
  );
}

function FullScreenLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <div className="flex-1 flex flex-col justify-center items-center px-4">
        <div key={location.pathname} className="w-full max-sm:max-w-full lg:max-w-[420px] bg-white rounded-2xl shadow-sm border border-background-200/60 p-8">
          {children}
        </div>
      </div>
    </div>
  );
}

const routes: RouteObject[] = [
  { path: "/login", element: <FullScreenLayout><Login /></FullScreenLayout> },
  { path: "/register", element: <FullScreenLayout><Register /></FullScreenLayout> },
  { path: "/forgot-password", element: <FullScreenLayout><ForgotPassword /></FullScreenLayout> },
  { path: "/onboarding", element: <FullScreenLayout><Onboarding /></FullScreenLayout> },
  { path: "/strategies", element: <SetupPage /> },

  { path: "/", element: <TabLayout><Dashboard /></TabLayout> },
  { path: "/watchlist", element: <TabLayout><WatchlistPage /></TabLayout> },
  { path: "/positions", element: <TabLayout><Positions /></TabLayout> },
  { path: "/stock/:code", element: <TabLayout><StockDetail /></TabLayout> },
  { path: "/decision/:id", element: <TabLayout><DecisionDetail /></TabLayout> },
  { path: "/news", element: <TabLayout><NewsHome /></TabLayout> },

  { path: "/research", element: <TabLayout><ResearchHome /></TabLayout> },
  { path: "/research/generate/:code", element: <TabLayout><ResearchGenerate /></TabLayout> },
  { path: "/research/report/:id", element: <TabLayout><ResearchReport /></TabLayout> },
  { path: "/research/library", element: <TabLayout><ResearchLibrary /></TabLayout> },

  { path: "/ai-trader", element: <TabLayout><AITrader /></TabLayout> },
  { path: "/ai-trader/memory", element: <TabLayout><AITraderMemory /></TabLayout> },
  { path: "/ai-strategy", element: <TabLayout><AIStrategy /></TabLayout> },
  { path: "/ai-strategy/create", element: <TabLayout><AIStrategyCreate /></TabLayout> },
  { path: "/ai-strategy/edit/:id", element: <TabLayout><AIStrategyEdit /></TabLayout> },
  { path: "/ai-strategy/detail/:id", element: <TabLayout><AIStrategyDetail /></TabLayout> },
  { path: "/ai-strategy/backtest/:id", element: <TabLayout><AIStrategyBacktest /></TabLayout> },

  { path: "/profile", element: <TabLayout><Profile /></TabLayout> },
  { path: "/profile/account", element: <TabLayout><ProfileAccount /></TabLayout> },
  { path: "/profile/subscription", element: <TabLayout><ProfileSubscription /></TabLayout> },
  { path: "/profile/stats", element: <TabLayout><ProfileStats /></TabLayout> },
  { path: "/profile/about", element: <TabLayout><About /></TabLayout> },
  { path: "/profile/about/user", element: <TabLayout><AboutUser /></TabLayout> },
  { path: "/profile/about/privacy", element: <TabLayout><AboutPrivacy /></TabLayout> },
  { path: "/profile/about/risk", element: <TabLayout><AboutRisk /></TabLayout> },
  { path: "/profile/about/contact", element: <TabLayout><AboutContact /></TabLayout> },
  { path: "/notifications", element: <TabLayout><Notifications /></TabLayout> },
  { path: "/achievements", element: <TabLayout><Achievements /></TabLayout> },
  { path: "/reports", element: <TabLayout><Reports /></TabLayout> },
  { path: "/calendar", element: <TabLayout><Calendar /></TabLayout> },
  { path: "/dashboard/flow", element: <DashboardFlowPage /> },
  { path: "/addon-pricing", element: <TabLayout><AboutContact /></TabLayout> },

  { path: "*", element: <NotFound /> },
];

export default routes;
